// Phase 84.M.34 — Self Rating Verification Modal
// Phase 84.M.36 — Bỏ branding nền tảng cụ thể (generic "nền tảng có điểm trình") +
// thêm progress UI + capture error context cho admin.
// Flow:
//   1) User chọn 1-3 ảnh → preview thumbnails.
//   2) Bấm "Phân tích" → POST /analyze → BE chạy AI OCR + compare.
//   3) Hiển thị kết quả: oldScore, proposedScore, match grid, statement.
//   4) User tick "Tôi đã hiểu rõ và đồng ý" → POST /submit.
//   5) Show success state — pending review.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X,
  UploadSimple,
  ImageSquare,
  CheckCircle,
  XCircle,
  Sparkle,
  ShieldCheck,
  Warning,
} from "@phosphor-icons/react";
import {
  analyzeRatingScreenshot,
  submitRatingVerification,
  fileToBase64NoPrefix,
  listMyRatingRequests,
} from "../../../lib/ratingVerificationApi";
import { resizedFileToBase64Payload } from "../../../lib/imageResize";
import { formatApiErrorDetail } from "../../../lib/authApi";
import InAppBrowserInlineWarning from "../../inapp/InAppBrowserInlineWarning";
import RatingVerifyIllustration from "./RatingVerifyIllustration";
import OCRFailureGuideModal from "./OCRFailureGuideModal";
import RatingVideoUploadModal from "./RatingVideoUploadModal";
import UploadConsentWizard from "../../shared/UploadConsentWizard";
import AIConsentModal from "../../moderation/AIConsentModal";

const MAX_IMAGES = 1; // Phase 85.A.93 — chỉ cho upload 1 ảnh OCR duy nhất.
const MAX_SIZE = 5 * 1024 * 1024;
const ACCEPT_TYPES = "image/jpeg,image/png,image/webp,image/heic,image/heif";

// Phase 85.A.69 — Session-local flag để biết user đã thấy guide modal
// chưa (user choice Q6=b: refresh page reset). Khi OCR fail lần đầu
// trong session → show modal carousel hướng dẫn chi tiết. Lần 2+ fail
// trong cùng session → chỉ toast (đã thấy guide).
const OCR_GUIDE_SHOWN_KEY = "pickleague:ocr_guide_shown_session";

const FIELD_LABELS = {
  phone: "Số điện thoại",
  region: "Khu vực",
  name: "Tên thật",
  gender: "Giới tính",
};

export default function SelfRatingVerificationModal({ open, onClose, onSubmitted }) {
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyze, setAnalyze] = useState(null); // { ocr_result_json, proposed_score, ... }
  const [commitment, setCommitment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedDoc, setSubmittedDoc] = useState(null);
  // Phase 84.M.36 — Progress UI cho upload + analyze stage.
  const [progress, setProgress] = useState({ stage: "idle", pct: 0, label: "" });
  const [lastError, setLastError] = useState(null);
  // Phase 85.A.69 — Modal hướng dẫn chi tiết khi OCR fail lần đầu/session.
  const [showGuideModal, setShowGuideModal] = useState(false);
  // Phase 85.A.83 — Consent wizard bắt buộc xem hướng dẫn step 1.
  // Mở modal lần nào → hiện wizard lần đó, user phải kéo slider 10s mới qua.
  const [consentPassed, setConsentPassed] = useState(false);
  // Phase 85.A.90 — Sau khi user fail OCR lần đầu, hiện checkbox + nút
  // "Upload Video thi đấu Pickleball của tôi" như fallback.
  const [noOtherPlatformChecked, setNoOtherPlatformChecked] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  // Phase 85.A.93 — đếm số lần phân tích để đổi label nút + trigger wizard 10s
  // duy nhất 1 lần sau lần fail đầu tiên (user choice 1c).
  const [attemptCount, setAttemptCount] = useState(0);
  const [retryWizardConsumed, setRetryWizardConsumed] = useState(false);
  // Lưu chữ ký file đã chọn để chặn chọn lại ảnh CŨ (chống spam).
  const [lastFileSig, setLastFileSig] = useState(null);
  // Phase 85.A.95 — Nếu user đã có ≥1 request BỊ REJECT trước đó → tự động
  // show khối "Upload Video" fallback ngay khi modal mở (không cần fail OCR
  // lại lần nữa).
  const [hasPreviousRejection, setHasPreviousRejection] = useState(false);
  // Phase 85.A.95 — Một khi đã fail OCR trong session → giữ video fallback hiện
  // mãi (không reset bởi onRetry của OCRFailureGuideModal).
  const [hasFailedThisSession, setHasFailedThisSession] = useState(false);
  // v16.61.1 (Feb 2026) — Apple 5.1.1(ii): AI consent gate cho OCR. HIỆN
  // MỖI LẦN user bấm "Phân tích" (không cache). Từ chối → chuyển sang flow
  // upload video thủ công (bypass AI).
  const [aiConsentPending, setAiConsentPending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setAnalyzing(false);
      setAnalyze(null);
      setCommitment(false);
      setSubmitting(false);
      setSubmittedDoc(null);
      setProgress({ stage: "idle", pct: 0, label: "" });
      setLastError(null);
      setConsentPassed(false);
      setNoOtherPlatformChecked(false);
      setShowVideoModal(false);
      setAttemptCount(0);
      setRetryWizardConsumed(false);
      setLastFileSig(null);
      setHasPreviousRejection(false);
      setHasFailedThisSession(false);
      setAiConsentPending(false);
      return;
    }
    // Phase 85.A.95 — Khi mở modal, check history reject để bật fallback video.
    let cancelled = false;
    (async () => {
      try {
        const data = await listMyRatingRequests();
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        const hasReject = items.some((x) => (x?.status || "").toLowerCase() === "rejected");
        if (!cancelled && hasReject) setHasPreviousRejection(true);
      } catch { /* ignore — fallback vẫn hoạt động theo lastError */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const addFiles = async (rawList) => {
    const arr = Array.from(rawList || []);
    if (arr.length === 0) return;
    // Phase 85.A.93 — chỉ giữ duy nhất 1 ảnh: ảnh mới REPLACE ảnh cũ.
    const f = arr[0];
    if (f.size > MAX_SIZE) {
      toast.error(`${f.name}: > 5MB`);
      return;
    }
    const sig = `${f.name}|${f.size}|${f.lastModified}`;
    if (sig === lastFileSig) {
      toast.error("Vui lòng chọn ảnh KHÁC (tránh spam ảnh trùng).");
      return;
    }
    try {
      const resized = await resizedFileToBase64Payload(f, { maxEdge: 1200, quality: 0.8 });
      // Revoke cũ trước khi replace
      files.forEach((old) => old?.preview && URL.revokeObjectURL(old.preview));
      setFiles([{ file: f, preview: URL.createObjectURL(f), ...resized }]);
      setLastFileSig(sig);
      setAnalyze(null);
      // Phase 85.A.93 — Nếu user đã FAIL OCR trước đó & chưa thấy wizard retry
      // lần nào → reset consentPassed để wizard 10s hiện lại đúng 1 lần.
      if (lastError && !retryWizardConsumed) {
        setConsentPassed(false);
      }
    } catch (e) {
      toast.error(`${f.name}: Không đọc/resize được ảnh`);
    }
  };

  const removeFile = () => {
    files.forEach((old) => old?.preview && URL.revokeObjectURL(old.preview));
    setFiles([]);
    setAnalyze(null);
    setLastFileSig(null);
  };

  // v16.61.1 — `runAnalyze` chỉ mở AI consent modal. Sau khi user tap
  // "Đồng ý", `doAnalyze` mới gọi API AI OCR thực sự. Nếu Decline → mở
  // luôn flow upload video thủ công (không qua AI) để user vẫn hoàn tất
  // được xác minh điểm trình.
  const runAnalyze = () => {
    if (files.length === 0) {
      toast.error("Vui lòng tải ảnh trước");
      return;
    }
    setAiConsentPending(true);
  };

  const doAnalyze = async () => {
    if (files.length === 0) {
      toast.error("Vui lòng tải ảnh trước");
      return;
    }
    setAnalyzing(true);
    setLastError(null);
    setAttemptCount((c) => c + 1);
    setProgress({ stage: "uploading", pct: 1, label: "Đang chuẩn bị tải ảnh lên..." });
    // Phase 85.A.98 — smooth progress giả lập cho giai đoạn AI đang chạy (BE
    // không stream progress thật, nên FE auto-tăng pct mỗi 400ms với caption
    // mô tả động để user không cảm thấy chờ).
    let analyzeTimer = null;
    let phaseTimer = null;
    try {
      const images = files.map((f) => ({ data: f.data, mime: f.mime }));
      const r = await analyzeRatingScreenshot(images, (pct) => {
        // axios upload progress (0..100) → map vào dải 1-30%.
        const mapped = Math.max(1, Math.round(pct * 0.30));
        setProgress({
          stage: "uploading",
          pct: mapped,
          label: pct < 100 ? `Đang tải ảnh lên cloud (${pct}%)...` : "Đã tải xong, đang gọi AI...",
        });
        if (pct >= 100 && !analyzeTimer) {
          // Bắt đầu giai đoạn analyzing: tăng dần 30 → 95.
          const startAt = Date.now();
          analyzeTimer = setInterval(() => {
            const elapsed = (Date.now() - startAt) / 1000;
            // Tăng nhanh 30→70 trong 4s, sau đó chậm dần đến 95 (asymptotic).
            const base = 30;
            const target = elapsed < 4 ? base + elapsed * 10 : 70 + (1 - Math.exp(-(elapsed - 4) / 6)) * 25;
            setProgress((cur) => ({
              ...cur,
              stage: "analyzing",
              pct: Math.min(95, Math.round(target)),
              label: cur?.label || "AI đang đọc tên & điểm trình...",
            }));
          }, 250);
          // Caption đổi theo giai đoạn để user thấy có "tiến triển".
          const phases = [
            { at: 1500, label: "AI đang đọc tên & điểm trình..." },
            { at: 5000, label: "Đang kiểm tra hợp lệ với hồ sơ của bạn..." },
            { at: 10_000, label: "Sắp xong, đang phân tích chi tiết..." },
            { at: 18_000, label: "Vui lòng chờ thêm vài giây nữa..." },
          ];
          phases.forEach((p) => {
            const t = setTimeout(() => {
              setProgress((cur) => ({ ...(cur || {}), label: p.label }));
            }, p.at);
            phaseTimer = phaseTimer || t;
          });
        }
      });
      if (analyzeTimer) { clearInterval(analyzeTimer); analyzeTimer = null; }
      setProgress({ stage: "done", pct: 100, label: "Hoàn tất 🎉" });
      setAnalyze(r);
    } catch (e) {
      if (analyzeTimer) clearInterval(analyzeTimer);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const url = e?.config?.url;
      const friendlyMsg = (typeof detail === "string" && detail.trim()) || "OCR thất bại — vui lòng thử lại";
      setLastError({ status, detail: friendlyMsg, url, raw: e?.message });
      setHasFailedThisSession(true);
      toast.error(friendlyMsg);
      setProgress({ stage: "idle", pct: 0, label: "" });
      try {
        const alreadyShown = window.sessionStorage.getItem(OCR_GUIDE_SHOWN_KEY) === "1";
        if (!alreadyShown) {
          window.sessionStorage.setItem(OCR_GUIDE_SHOWN_KEY, "1");
          setShowGuideModal(true);
        }
      } catch { /* ignore sessionStorage disabled */ }
    } finally {
      if (analyzeTimer) clearInterval(analyzeTimer);
      setAnalyzing(false);
    }
  };

  const runSubmit = async () => {
    if (!analyze) return;
    if (!commitment) {
      toast.error("Vui lòng tick cam kết");
      return;
    }
    setSubmitting(true);
    setProgress({ stage: "submitting", pct: 0, label: "Đang lưu yêu cầu..." });
    try {
      const images = files.map((f) => ({ data: f.data, mime: f.mime }));
      const doc = await submitRatingVerification({
        images,
        ocr_result_json: analyze.ocr_result_json,
        ocr_raw_text: analyze.ocr_raw_text,
        ocr_confidence: analyze.ocr_confidence,
        commitment: true,
      }, (pct) => setProgress({ stage: "submitting", pct, label: `Đang gửi ${pct}%...` }));
      setSubmittedDoc(doc);
      onSubmitted?.(doc);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const friendlyMsg = (typeof detail === "string" && detail.trim()) || "Gửi thất bại";
      setLastError({ status, detail: friendlyMsg, raw: e?.message });
      toast.error(friendlyMsg);
      setProgress({ stage: "idle", pct: 0, label: "" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur" data-testid="self-rating-modal">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto bg-[#0B1220] border-t border-white/10 sm:border sm:rounded-3xl rounded-t-3xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-[#0B1220]/95 backdrop-blur border-b border-white/8">
          <div>
            <h2 className="text-[16px] font-black text-white tracking-tight">Gửi ảnh điểm trình hiện tại của bạn</h2>
            <p className="text-[10.5px] text-white/55 mt-0.5">Screenshot từ DUPR / BaseLine / nền tảng khác</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center"
            data-testid="self-rating-close"
            aria-label="Đóng"
          >
            <X size={16} weight="bold" className="text-white/80" />
          </button>
        </div>

        {submittedDoc ? (
          <SubmittedState doc={submittedDoc} onClose={onClose} />
        ) : (
          <div className="p-4 space-y-4">
            {/* Phase 84.M.102 — Hero illustration giúp user hiểu ngay loại ảnh cần upload. */}
            <RatingVerifyIllustration testId="self-rating-illustration" />
            {/* Phase 84.M.86 — In-app browser warning (upload có thể fail). */}
            <InAppBrowserInlineWarning flow="upload" />
            {/* Phase 85.A.93 — Hướng dẫn loại ảnh: highlight ĐỎ + icon Warning,
                animation pulse để gây chú ý. */}
            <div
              className="rounded-2xl border-2 border-rose-400/65 bg-rose-500/12 p-3.5 text-[13.5px] text-white leading-relaxed font-medium flex items-start gap-2.5 shadow-[0_0_0_3px_rgba(244,63,94,0.08)]"
              data-testid="self-rating-warning-screenshot"
            >
              <Warning size={20} weight="fill" className="text-rose-300 shrink-0 mt-0.5 animate-pulse" />
              <div>
                Nộp <span className="text-rose-200 font-black">ảnh chụp màn hình (screenshot)</span> ảnh có
                <span className="text-rose-200 font-black"> điểm trình trên nền tảng KHÁC</span> mà bạn đã từng tham gia giải đấu (DUPR, BaseLine, ...).
                Ảnh cần thấy rõ <span className="text-white font-black">Nickname · Tên · Giới tính · SĐT · Khu vực · tab "Điểm đôi" đang chọn + Điểm hiện tại</span>.
              </div>
            </div>

            {/* Upload — Phase 85.A.93: chỉ 1 ảnh duy nhất, button "Phân tích" /
                "Gửi lại ảnh duyệt điểm trình" đặt ngay bên dưới upload area.
                Phase 85.A.99 — rút gọn chiều cao box (h-32) để không chiếm
                hết màn hình mobile. */}
            <div>
              {files.length > 0 ? (
                <div className="relative w-full h-24 rounded-2xl overflow-hidden border-2 border-white/15 bg-black/40" data-testid="self-rating-preview-0">
                  <img src={files[0].preview} alt="" className="w-full h-full object-contain bg-black/60" />
                  <button
                    type="button"
                    onClick={removeFile}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/75 backdrop-blur hover:bg-rose-500/80 flex items-center justify-center transition"
                    aria-label="Xoá ảnh"
                    data-testid="self-rating-remove-photo"
                  >
                    <X size={12} weight="bold" className="text-white" />
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="absolute bottom-1.5 left-1.5 px-2.5 h-7 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur text-white text-[10.5px] font-bold flex items-center gap-1.5 transition"
                    data-testid="self-rating-replace-photo"
                  >
                    <UploadSimple size={11} weight="bold" /> Chọn ảnh khác
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-20 rounded-2xl border-2 border-dashed border-white/25 hover:border-[#FF8A00]/70 hover:bg-[#FF8A00]/5 transition flex items-center justify-center gap-2.5 text-white/70 px-3"
                  data-testid="self-rating-add-photo"
                >
                  <UploadSimple size={18} weight="bold" className="shrink-0" />
                  <div className="text-left">
                    <div className="text-[12.5px] font-black leading-tight">Chọn ảnh từ máy</div>
                    <div className="text-[9.5px] text-white/45 leading-tight mt-0.5">Chỉ 1 ảnh · JPG/PNG/WEBP/HEIC · ≤ 5MB</div>
                  </div>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT_TYPES}
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = ""; // cho phép re-pick cùng tên ảnh khác lastModified.
                }}
              />
              <p className="text-[10px] text-white/40 mt-1.5">Chỉ chấp nhận 1 ảnh screenshot mỗi lần. Ảnh mới sẽ thay thế ảnh đang chọn.</p>
            </div>

            {!analyze && (
              <>
                {/* Progress bar — Phase 85.A.103: di chuyển LÊN TRƯỚC nút
                    để user thấy ngay tiến trình mà không phải scroll. */}
                {(progress.stage === "uploading" || progress.stage === "analyzing") && (
                  <div
                    className="rounded-2xl border border-[#FF8A00]/40 bg-gradient-to-br from-[#FF8A00]/10 to-[#FF9F1C]/[0.04] p-4 space-y-2.5"
                    data-testid="self-rating-progress"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-4 h-4 rounded-full border-2 border-[#FFB300] border-t-transparent animate-spin shrink-0" />
                        <span className="text-[12.5px] font-bold text-amber-100 truncate" data-testid="self-rating-progress-label">
                          {progress.label || "Đang xử lý..."}
                        </span>
                      </div>
                      <span className="text-[13px] font-black text-[#FFB300] tabular-nums shrink-0" data-testid="self-rating-progress-pct">
                        {Math.min(100, Math.max(0, Math.round(progress.pct || 0)))}%
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-black/40 overflow-hidden border border-white/5">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#FF8A00] via-[#FF9F1C] to-emerald-400 transition-all duration-500 ease-out"
                        style={{ width: `${Math.min(100, Math.max(0, progress.pct || 0))}%` }}
                        data-testid="self-rating-progress-bar"
                      />
                    </div>
                    <div className="text-[10.5px] text-white/55 leading-snug">
                      💡 Vui lòng chờ trong giây lát — AI đang đọc thông tin trên ảnh để xác thực.
                    </div>
                  </div>
                )}

                {/* Phase 85.A.93 — Nút Phân tích / Gửi lại đặt NGAY GẦN upload area. */}
                <button
                  type="button"
                  onClick={runAnalyze}
                  disabled={files.length === 0 || analyzing}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white font-black text-[13px] tracking-wide disabled:opacity-40 inline-flex items-center justify-center gap-2 shadow-[0_8px_22px_rgba(255,138,0,0.35)]"
                  data-testid="self-rating-analyze-btn"
                >
                  {analyzing ? (
                    <><Sparkle size={14} weight="bold" className="animate-pulse" /> Đang phân tích ảnh…</>
                  ) : attemptCount >= 1 ? (
                    <><UploadSimple size={14} weight="bold" /> Gửi lại ảnh duyệt điểm trình</>
                  ) : (
                    <><ImageSquare size={14} weight="bold" /> Phân tích ảnh</>
                  )}
                </button>

                {/* Error block — chi tiết hơn 1 dòng toast */}
                {lastError && !analyzing && (
                  <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-[11.5px] text-rose-200 space-y-1" data-testid="self-rating-error-block">
                    <div className="font-black text-rose-100 flex items-center gap-1.5"><XCircle size={12} weight="fill" /> {lastError.detail}</div>
                    {(lastError.status || lastError.url) && (
                      <div className="text-[10px] opacity-80 font-mono">
                        {lastError.status ? `${lastError.status} ` : ""}{lastError.url || ""}
                      </div>
                    )}
                  </div>
                )}

                {/* Phase 85.A.90 / 85.A.95 — Fallback Video hiện khi:
                    - user vừa fail OCR (lastError), HOẶC
                    - user đã từng fail trong session này (hasFailedThisSession), HOẶC
                    - user có request bị REJECT từ trước (hasPreviousRejection). */}
                {(lastError || hasFailedThisSession || hasPreviousRejection) && !analyzing && (
                  <div
                    className="rounded-2xl border border-blue-400/35 bg-gradient-to-br from-blue-500/10 to-cyan-500/[0.04] p-3.5 space-y-3"
                    data-testid="self-rating-video-fallback"
                  >
                    <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-blue-300/85 flex items-center gap-1.5">
                      <Sparkle size={11} weight="fill" />
                      Phương án khác
                    </div>
                    <p className="text-[12.5px] text-white/85 leading-relaxed">
                      Nếu bạn <strong className="text-white">chưa từng tham gia</strong> các nền tảng đánh giải nào trước đây, bạn có thể nộp <strong className="text-blue-200">VIDEO thi đấu Pickleball</strong> để BTC tự chấm điểm trình cho bạn.
                    </p>
                    <label className="flex items-start gap-2.5 p-2.5 rounded-xl border border-white/10 hover:border-blue-400/40 bg-white/[0.03] cursor-pointer transition" data-testid="self-rating-no-other-platform-label">
                      <input
                        type="checkbox"
                        checked={noOtherPlatformChecked}
                        onChange={(e) => setNoOtherPlatformChecked(e.target.checked)}
                        className="w-4 h-4 mt-0.5 shrink-0 accent-blue-500"
                        data-testid="self-rating-no-other-platform-checkbox"
                      />
                      <span className="text-[12px] text-white/90 font-medium leading-snug">
                        Tôi xác nhận <strong>CHƯA từng tham gia PVNA / BaseLine / DUPR</strong> đánh giải nào.
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowVideoModal(true)}
                      disabled={!noOtherPlatformChecked}
                      className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white text-[12.5px] font-black flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_6px_18px_rgba(59,130,246,0.4)]"
                      data-testid="self-rating-open-video-modal"
                    >
                      <Sparkle size={13} weight="fill" />
                      Upload Video thi đấu Pickleball của tôi
                    </button>
                  </div>
                )}
              </>
            )}

            {analyze && <PreviewBlock data={analyze} commitment={commitment} setCommitment={setCommitment} onSubmit={runSubmit} submitting={submitting} />}
          </div>
        )}
      </div>

      {/* Phase 85.A.69 — Modal hướng dẫn chi tiết khi OCR fail lần đầu/session */}
      <OCRFailureGuideModal
        open={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        onRetry={() => {
          // Phase 85.A.97 — Khi user bấm "Tôi đã hiểu — Thử lại":
          //  1. Clear ảnh cũ khỏi state (revoke object URL trước để tránh leak).
          //  2. Giữ `lastFileSig` để chặn user chọn LẠI đúng ảnh đó (tránh spam).
          //  3. Reset error block. Giữ `hasFailedThisSession` để khối Video fallback
          //     vẫn hiển thị cho user.
          files.forEach((f) => f?.preview && URL.revokeObjectURL(f.preview));
          setFiles([]);
          setAnalyze(null);
          setLastError(null);
        }}
        errorDetail={lastError?.detail}
      />

      {/* Phase 85.A.83 — Wizard bước 1 BẮT BUỘC xem hướng dẫn OCR rating
          mỗi lần user mở modal này. User phải kéo slider 10s mới được upload. */}
      <UploadConsentWizard
        open={open && !consentPassed}
        type="rating"
        onClose={() => {
          // Nếu user đóng wizard mà chưa agree → đóng luôn modal chính.
          onClose?.();
        }}
        onAgree={() => {
          // Phase 85.A.93 — Nếu đây là wizard hiện ra sau khi user đã fail
          // (lastError tồn tại + chưa consume retry wizard) → mark consumed
          // để các lần fail tiếp theo không hiện wizard nữa.
          if (lastError && !retryWizardConsumed) {
            setRetryWizardConsumed(true);
          }
          setConsentPassed(true);
        }}
      />

      {/* Phase 85.A.90 — Video upload modal (fallback khi OCR fail) */}
      <RatingVideoUploadModal
        open={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        onSubmitted={(video) => {
          // Đóng cả 2 modal khi user nộp thành công.
          setShowVideoModal(false);
          onSubmitted?.(video);
          onClose?.();
        }}
      />

      {/* v16.61.1 — Apple 5.1.1(ii) AI consent gate cho OCR. HIỆN MỖI LẦN
          user bấm "Phân tích". Đồng ý → gọi AI OCR. Từ chối → mở flow
          upload video thủ công (bypass AI) để user vẫn hoàn tất xác minh. */}
      <AIConsentModal
        open={aiConsentPending}
        onAgree={() => {
          setAiConsentPending(false);
          doAnalyze();
        }}
        onDecline={() => {
          setAiConsentPending(false);
          toast.message("Bạn có thể xác minh bằng cách tải lên video thi đấu thay vì AI.");
          setShowVideoModal(true);
        }}
      />
    </div>
  );
}

function PreviewBlock({ data, commitment, setCommitment, onSubmit, submitting }) {
  const today = new Date().toLocaleDateString("vi-VN");
  const mr = data.match_result_json || {};
  const ocr = data.ocr_result_json || {};

  return (
    <div className="space-y-3" data-testid="self-rating-preview">
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/8 p-3.5">
        <div className="text-[10.5px] uppercase tracking-widest text-emerald-300 font-black mb-1">Kết quả AI phân tích</div>
        <p className="text-[12.5px] text-white leading-relaxed">
          Điểm của bạn trên hệ thống cũ là <span className="font-black text-emerald-300 tabular-nums">{Number(data.old_score).toFixed(3)}</span>.<br />
          Nếu đây đúng là điểm hiện tại của bạn hôm nay ngày <span className="font-bold">{today}</span>, hệ thống đề xuất điểm mới của bạn là{" "}
          <span className="font-black text-[#FF8A00] tabular-nums">{Number(data.proposed_score).toFixed(3)}</span>.
        </p>
      </div>

      {/* Match grid */}
      <div>
        <div className="text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">Đối chiếu hồ sơ</div>
        <div className="grid grid-cols-2 gap-2" data-testid="self-rating-match-grid">
          {["phone", "region", "name", "gender"].map((k) => (
            <div key={k} className={`rounded-xl px-3 py-2 border ${mr[k] ? "border-emerald-500/35 bg-emerald-500/8" : "border-rose-500/35 bg-rose-500/8"}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-bold">
                {mr[k] ? (
                  <CheckCircle size={13} weight="fill" className="text-emerald-300" />
                ) : (
                  <XCircle size={13} weight="fill" className="text-rose-400" />
                )}
                <span className={mr[k] ? "text-emerald-200" : "text-rose-300"}>{FIELD_LABELS[k]}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.auto_eligible && (
        <div className="rounded-xl border border-[#34D8FF]/30 bg-[#34D8FF]/8 px-3 py-2 flex items-center gap-2 text-[11.5px] text-[#34D8FF] font-bold" data-testid="self-rating-auto-eligible">
          <Sparkle size={12} weight="fill" /> Đủ điều kiện duyệt tự động — admin sẽ duyệt nhanh chóng.
        </div>
      )}

      {!mr.all_match && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/8 px-3 py-2 flex items-start gap-2 text-[11.5px] text-amber-200">
          <Warning size={13} weight="bold" className="mt-0.5 shrink-0" />
          <span>Một số thông tin chưa khớp với hồ sơ. Admin sẽ kiểm tra thủ công trước khi duyệt.</span>
        </div>
      )}

      {/* Phase 85.A.82 — Commitment với cảnh báo khoá tài khoản. */}
      <label className="block rounded-2xl border border-amber-400/40 bg-amber-500/[0.06] p-3 cursor-pointer">
        <div className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={commitment}
            onChange={(e) => setCommitment(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#FF8A00]"
            data-testid="self-rating-commitment"
          />
          <div className="text-[12px] text-white/90 leading-relaxed">
            <div className="font-black text-white mb-0.5 flex items-center gap-1.5">
              <ShieldCheck size={12} weight="fill" className="text-[#FF8A00]" /> Cam kết
            </div>
            Tôi cam kết đây là ảnh điểm trình của tôi trên nền tảng KHÁC. Tôi hiểu rằng nếu cố tình
            không tải đúng ảnh sẽ chỉ làm quá trình xét duyệt thêm kéo dài, làm tốn thêm thời gian
            hoặc thậm chí <span className="font-black text-rose-300 bg-rose-500/15 px-1 rounded">bị khoá tài khoản</span>.
          </div>
        </div>
      </label>

      <button
        type="button"
        onClick={onSubmit}
        disabled={!commitment || submitting}
        className="w-full h-11 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white font-black text-[13px] tracking-wide disabled:opacity-40"
        data-testid="self-rating-submit"
      >
        {submitting ? "Đang gửi…" : "Gửi yêu cầu xét duyệt"}
      </button>
    </div>
  );
}

function SubmittedState({ doc, onClose }) {
  return (
    <div className="p-6 text-center space-y-3" data-testid="self-rating-success">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
        <CheckCircle size={32} weight="fill" className="text-emerald-300" />
      </div>
      <h3 className="text-[16px] font-black text-white">Đã gửi yêu cầu</h3>
      <p className="text-[12px] text-white/70 leading-relaxed">
        Yêu cầu của bạn (mã <span className="font-mono text-emerald-300">{(doc?.id || "").slice(0, 8)}</span>) đang ở trạng thái{" "}
        <span className="font-bold text-[#FF8A00] uppercase">{doc?.status === "needs_review" ? "Cần xem lại" : "Chờ duyệt"}</span>.
        Em sẽ thông báo ngay khi admin xét duyệt xong.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-3 px-6 h-10 rounded-full bg-white/10 text-white font-bold text-[12.5px]"
      >
        Đóng
      </button>
    </div>
  );
}
